import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import AppHeader from '../components/ui/AppHeader';
import {
  UtmCampaign,
  UtmBuilderState,
  generateUtmUrl,
  WEBSITE_DOMAINS,
  COMMON_UTM_SOURCES,
  COMMON_UTM_MEDIUMS,
  CAMPAIGN_STATUS_COLORS,
  CAMPAIGN_COLORS,
} from '../types/utmStrategy';
import { Target } from 'lucide-react';
import './UTMStrategyBuilder.css';

const UTMStrategyBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    utmStrategies,
    createUtmStrategy,
    deleteUtmStrategy,
    addCampaignToStrategy,
    updateCampaign,
    deleteCampaign,
    addLinkToCampaign,
    deleteLinkFromCampaign,
    addNoteToCampaign,
    deleteNoteFromCampaign,
    leads,
  } = useCRM();

  // Helper function to get website label from baseUrl
  const getWebsiteLabel = (baseUrl: string): string => {
    const normalizedUrl = baseUrl.toLowerCase();
    if (normalizedUrl.includes('build.lctimberframes.com')) {
      return '3D Kits';
    } else if (normalizedUrl.includes('lctimberframes.com')) {
      return 'Main Website';
    }
    return 'Custom';
  };

  // State
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
  const [showCreateStrategy, setShowCreateStrategy] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [newStrategyName, setNewStrategyName] = useState('');
  const [newStrategyDescription, setNewStrategyDescription] = useState('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignDescription, setNewCampaignDescription] = useState('');
  const [selectedCampaignColor, setSelectedCampaignColor] = useState(CAMPAIGN_COLORS[0]);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
  const [campaignNotes, setCampaignNotes] = useState<Record<string, string>>({});
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  // UTM Builder state
  const [builderState, setBuilderState] = useState<UtmBuilderState>({
    baseUrl: WEBSITE_DOMAINS[0].domain,
    source: '',
    medium: '',
    campaign: '',
    term: '',
    content: '',
  });

  // Get selected strategy
  const selectedStrategy = useMemo(() => {
    return utmStrategies.find(s => s.id === selectedStrategyId) || null;
  }, [utmStrategies, selectedStrategyId]);

  // Calculate UTM analytics from leads
  const utmAnalytics = useMemo(() => {
    const sourceMap = new Map<string, number>();
    const mediumMap = new Map<string, number>();
    const campaignMap = new Map<string, number>();

    leads.forEach(lead => {
      const source = lead.trackingData?.utmSource;
      const medium = lead.trackingData?.utmMedium;
      const campaign = lead.trackingData?.utmCampaign;

      if (source) sourceMap.set(source, (sourceMap.get(source) || 0) + 1);
      if (medium) mediumMap.set(medium, (mediumMap.get(medium) || 0) + 1);
      if (campaign) campaignMap.set(campaign, (campaignMap.get(campaign) || 0) + 1);
    });

    return {
      sources: Array.from(sourceMap.entries()).sort((a, b) => b[1] - a[1]),
      mediums: Array.from(mediumMap.entries()).sort((a, b) => b[1] - a[1]),
      campaigns: Array.from(campaignMap.entries()).sort((a, b) => b[1] - a[1]),
    };
  }, [leads]);

  // Generate URL preview
  const generatedUrl = useMemo(() => {
    return generateUtmUrl(builderState);
  }, [builderState]);

  // Collect previously used UTM values from all campaigns
  const previouslyUsedValues = useMemo(() => {
    const sources = new Set<string>();
    const mediums = new Set<string>();
    const campaigns = new Set<string>();
    const terms = new Set<string>();
    const contents = new Set<string>();

    utmStrategies.forEach(strategy => {
      strategy.campaigns.forEach(campaign => {
        campaign.links.forEach(link => {
          if (link.utmSource) sources.add(link.utmSource);
          if (link.utmMedium) mediums.add(link.utmMedium);
          if (link.utmCampaign) campaigns.add(link.utmCampaign);
          if (link.utmTerm) terms.add(link.utmTerm);
          if (link.utmContent) contents.add(link.utmContent);
        });
      });
    });

    return {
      sources: Array.from(sources).sort(),
      mediums: Array.from(mediums).sort(),
      campaigns: Array.from(campaigns).sort(),
      terms: Array.from(terms).sort(),
      contents: Array.from(contents).sort(),
    };
  }, [utmStrategies]);

  // Handlers
  const handleCreateStrategy = async () => {
    if (!newStrategyName.trim() || !user) return;

    try {
      // Check Firebase auth state
      const { auth } = await import('../utils/firebase');
      const currentUser = auth.currentUser;

      if (!currentUser) {
        alert('You must be logged in to create a strategy. Please refresh the page and log in again.');
        return;
      }

      const id = await createUtmStrategy({
        name: newStrategyName.trim(),
        description: newStrategyDescription.trim() || undefined,
        campaigns: [],
        sharedWith: [],
        createdBy: user.id,
        updatedBy: user.id,
      });
      setSelectedStrategyId(id);
      setShowCreateStrategy(false);
      setNewStrategyName('');
      setNewStrategyDescription('');
    } catch (err) {
      console.error('Error creating strategy:', err);
      alert(`Error creating strategy: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim() || !selectedStrategyId || !user) return;

    try {
      await addCampaignToStrategy(selectedStrategyId, {
        name: newCampaignName.trim(),
        description: newCampaignDescription.trim() || undefined,
        color: selectedCampaignColor,
        status: 'draft',
        links: [],
        notes: [],
        tags: [],
        createdBy: user.id,
        updatedBy: user.id,
      });
      setShowCreateCampaign(false);
      setNewCampaignName('');
      setNewCampaignDescription('');
      setSelectedCampaignColor(CAMPAIGN_COLORS[0]);
    } catch (err) {
      console.error('Error creating campaign:', err);
    }
  };

  const handleAddLinkToCampaign = async (campaignId: string) => {
    if (!selectedStrategyId || !user) return;

    try {
      // Parse comma-separated values for bulk creation
      const terms = builderState.term 
        ? builderState.term.split(',').map(t => t.trim()).filter(t => t)
        : [''];
      const contents = builderState.content 
        ? builderState.content.split(',').map(c => c.trim()).filter(c => c)
        : [''];

      // Create a link for each combination
      for (const term of terms) {
        for (const content of contents) {
          // Generate URL for this specific combination
          const linkState = {
            ...builderState,
            term,
            content,
          };
          const url = generateUtmUrl(linkState);
          
          if (url) {
            await addLinkToCampaign(selectedStrategyId, campaignId, {
              baseUrl: builderState.baseUrl,
              utmSource: builderState.source,
              utmMedium: builderState.medium,
              utmCampaign: builderState.campaign,
              utmTerm: term || undefined,
              utmContent: content || undefined,
              generatedUrl: url,
              createdBy: user.id,
            });
          }
        }
      }
      
      // Keep builder state populated for quick variations
    } catch (err) {
      console.error('Error adding link:', err);
    }
  };

  const handleDeleteLink = async (campaignId: string, linkId: string) => {
    if (!selectedStrategyId) return;
    if (!window.confirm('Delete this UTM link?')) return;

    try {
      await deleteLinkFromCampaign(selectedStrategyId, campaignId, linkId);
    } catch (err) {
      console.error('Error deleting link:', err);
    }
  };

  const handleAddNote = async (campaignId: string) => {
    const noteContent = campaignNotes[campaignId]?.trim();
    if (!noteContent || !selectedStrategyId || !user) return;

    try {
      await addNoteToCampaign(selectedStrategyId, campaignId, {
        content: noteContent,
        createdBy: user.id,
        createdByName: user.name || user.email || 'Unknown',
      });
      setCampaignNotes(prev => ({ ...prev, [campaignId]: '' }));
    } catch (err) {
      console.error('Error adding note:', err);
    }
  };

  const handleDeleteNote = async (campaignId: string, noteId: string) => {
    if (!selectedStrategyId) return;
    if (!window.confirm('Delete this note?')) return;

    try {
      await deleteNoteFromCampaign(selectedStrategyId, campaignId, noteId);
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const toggleCampaignExpanded = (campaignId: string) => {
    setExpandedCampaigns(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const handleUpdateCampaignStatus = async (campaignId: string, status: UtmCampaign['status']) => {
    if (!selectedStrategyId) return;
    try {
      await updateCampaign(selectedStrategyId, campaignId, { status });
    } catch (err) {
      console.error('Error updating campaign status:', err);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!selectedStrategyId) return;
    if (!window.confirm('Delete this campaign and all its links?')) return;

    try {
      await deleteCampaign(selectedStrategyId, campaignId);
    } catch (err) {
      console.error('Error deleting campaign:', err);
    }
  };

  const handleDeleteStrategy = async () => {
    if (!selectedStrategyId) return;
    if (!window.confirm('Delete this entire strategy? This cannot be undone.')) return;

    try {
      await deleteUtmStrategy(selectedStrategyId);
      setSelectedStrategyId(null);
    } catch (err) {
      console.error('Error deleting strategy:', err);
    }
  };

  return (
    <div className="utm-strategy-page">
      <AppHeader />

      <div className="utm-strategy-content">
        {/* Left Sidebar - Strategy List */}
        <div className="utm-sidebar">
          <div className="sidebar-header">
            <h3>Strategies</h3>
            <button 
              className="create-strategy-btn"
              onClick={() => setShowCreateStrategy(true)}
            >
              + New
            </button>
          </div>

          <div className="strategy-list">
            {utmStrategies.length === 0 ? (
              <div className="empty-strategies">
                <p>No strategies yet</p>
                <p className="hint">Create your first UTM strategy to organize your campaigns</p>
              </div>
            ) : (
              utmStrategies.map(strategy => (
                <div
                  key={strategy.id}
                  className={`strategy-item ${selectedStrategyId === strategy.id ? 'active' : ''}`}
                  onClick={() => setSelectedStrategyId(strategy.id)}
                >
                  <div className="strategy-item-name">{strategy.name}</div>
                  <div className="strategy-item-meta">
                    {strategy.campaigns.length} campaigns
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Quick Stats */}
          <div className="sidebar-stats">
            <h4>Active UTMs (from leads)</h4>
            <div className="stat-section">
              <div className="stat-label">Top Sources</div>
              {utmAnalytics.sources.slice(0, 5).map(([source, count]) => (
                <div key={source} className="stat-row">
                  <span>{source}</span>
                  <span className="stat-count">{count}</span>
                </div>
              ))}
            </div>
            <div className="stat-section">
              <div className="stat-label">Top Campaigns</div>
              {utmAnalytics.campaigns.slice(0, 5).map(([campaign, count]) => (
                <div key={campaign} className="stat-row">
                  <span>{campaign}</span>
                  <span className="stat-count">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <button 
            className="back-to-analytics-btn"
            onClick={() => navigate('/analytics')}
          >
            Back to Analytics
          </button>
        </div>

        {/* Main Content */}
        <div className="utm-main">
          {!selectedStrategy ? (
            <div className="no-strategy-selected">
              <div className="empty-state-icon"><Target size={48} /></div>
              <h2>Select or Create a Strategy</h2>
              <p>Choose a strategy from the sidebar or create a new one to start building UTM campaigns</p>
              <button 
                className="create-first-strategy-btn"
                onClick={() => setShowCreateStrategy(true)}
              >
                Create Your First Strategy
              </button>
            </div>
          ) : (
            <>
              {/* Strategy Header */}
              <div className="strategy-header">
                <div className="strategy-header-left">
                  <h1>{selectedStrategy.name}</h1>
                  {selectedStrategy.description && (
                    <p className="strategy-description">{selectedStrategy.description}</p>
                  )}
                </div>
                <div className="strategy-header-actions">
                  <button 
                    className="add-campaign-btn"
                    onClick={() => setShowCreateCampaign(true)}
                  >
                    + Add Campaign
                  </button>
                  <button 
                    className="delete-strategy-btn"
                    onClick={handleDeleteStrategy}
                  >
                    Delete Strategy
                  </button>
                </div>
              </div>

              {/* UTM Builder Section */}
              <div className="utm-builder-section">
                <h2>UTM Link Builder</h2>
                <div className="builder-grid">
                  {/* Base URL */}
                  <div className="builder-field">
                    <label>Base URL</label>
                    <div className="domain-buttons">
                      {WEBSITE_DOMAINS.map(domain => (
                        <button
                          key={domain.id}
                          className={`domain-btn ${builderState.baseUrl === domain.domain ? 'active' : ''}`}
                          onClick={() => setBuilderState(prev => ({ ...prev, baseUrl: domain.domain }))}
                        >
                          {domain.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={builderState.baseUrl}
                      onChange={(e) => setBuilderState(prev => ({ ...prev, baseUrl: e.target.value }))}
                      placeholder="Or enter custom URL"
                    />
                  </div>

                  {/* Source */}
                  <div className="builder-field">
                    <label>UTM Source <span className="required">*</span></label>
                    <div className="suggestion-chips">
                      {/* Preset values */}
                      {COMMON_UTM_SOURCES.slice(0, 6).map(source => (
                        <button
                          key={source}
                          className={`chip ${builderState.source === source ? 'active' : ''}`}
                          onClick={() => setBuilderState(prev => ({ ...prev, source }))}
                        >
                          {source}
                        </button>
                      ))}
                      {/* Previously used values (excluding presets) */}
                      {previouslyUsedValues.sources
                        .filter(source => !COMMON_UTM_SOURCES.includes(source as any))
                        .slice(0, 4)
                        .map(source => (
                          <button
                            key={`prev-${source}`}
                            className={`chip chip-previous ${builderState.source === source ? 'active' : ''}`}
                            onClick={() => setBuilderState(prev => ({ ...prev, source }))}
                          >
                            {source}
                          </button>
                        ))}
                    </div>
                    <input
                      type="text"
                      value={builderState.source}
                      onChange={(e) => setBuilderState(prev => ({ ...prev, source: e.target.value }))}
                      placeholder="e.g., facebook, google, newsletter"
                    />
                  </div>

                  {/* Medium */}
                  <div className="builder-field">
                    <label>UTM Medium <span className="required">*</span></label>
                    <div className="suggestion-chips">
                      {/* Preset values */}
                      {COMMON_UTM_MEDIUMS.slice(0, 6).map(medium => (
                        <button
                          key={medium}
                          className={`chip ${builderState.medium === medium ? 'active' : ''}`}
                          onClick={() => setBuilderState(prev => ({ ...prev, medium }))}
                        >
                          {medium}
                        </button>
                      ))}
                      {/* Previously used values (excluding presets) */}
                      {previouslyUsedValues.mediums
                        .filter(medium => !COMMON_UTM_MEDIUMS.includes(medium as any))
                        .slice(0, 4)
                        .map(medium => (
                          <button
                            key={`prev-${medium}`}
                            className={`chip chip-previous ${builderState.medium === medium ? 'active' : ''}`}
                            onClick={() => setBuilderState(prev => ({ ...prev, medium }))}
                          >
                            {medium}
                          </button>
                        ))}
                    </div>
                    <input
                      type="text"
                      value={builderState.medium}
                      onChange={(e) => setBuilderState(prev => ({ ...prev, medium: e.target.value }))}
                      placeholder="e.g., cpc, email, social"
                    />
                  </div>

                  {/* Campaign */}
                  <div className="builder-field">
                    <label>UTM Campaign <span className="required">*</span></label>
                    {previouslyUsedValues.campaigns.length > 0 && (
                      <div className="suggestion-chips">
                        {previouslyUsedValues.campaigns.slice(0, 6).map(campaign => (
                          <button
                            key={`prev-${campaign}`}
                            className={`chip chip-previous ${builderState.campaign === campaign ? 'active' : ''}`}
                            onClick={() => setBuilderState(prev => ({ ...prev, campaign }))}
                          >
                            {campaign}
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      type="text"
                      value={builderState.campaign}
                      onChange={(e) => setBuilderState(prev => ({ ...prev, campaign: e.target.value }))}
                      placeholder="e.g., spring_sale, product_launch"
                    />
                  </div>

                  {/* Term (Optional) */}
                  <div className="builder-field">
                    <label>UTM Term <span className="optional">(optional)</span></label>
                    {previouslyUsedValues.terms.length > 0 && (
                      <div className="suggestion-chips">
                        {previouslyUsedValues.terms.slice(0, 6).map(term => (
                          <button
                            key={`prev-${term}`}
                            className={`chip chip-previous ${builderState.term === term ? 'active' : ''}`}
                            onClick={() => setBuilderState(prev => ({ ...prev, term }))}
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      type="text"
                      value={builderState.term}
                      onChange={(e) => setBuilderState(prev => ({ ...prev, term: e.target.value }))}
                      placeholder="e.g., keyword, audience"
                    />
                  </div>

                  {/* Content (Optional) */}
                  <div className="builder-field">
                    <label>UTM Content <span className="optional">(optional)</span></label>
                    {previouslyUsedValues.contents.length > 0 && (
                      <div className="suggestion-chips">
                        {previouslyUsedValues.contents.slice(0, 6).map(content => (
                          <button
                            key={`prev-${content}`}
                            className={`chip chip-previous ${builderState.content === content ? 'active' : ''}`}
                            onClick={() => setBuilderState(prev => ({ ...prev, content }))}
                          >
                            {content}
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      type="text"
                      value={builderState.content}
                      onChange={(e) => setBuilderState(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="e.g., banner, sidebar_link"
                    />
                  </div>
                </div>

                {/* Generated URL Preview */}
                <div className="generated-url-section">
                  <label>Generated URL</label>
                  <div className="url-preview">
                    {generatedUrl ? (
                      <>
                        <code>{generatedUrl}</code>
                        <button 
                          className="copy-btn"
                          onClick={() => handleCopyUrl(generatedUrl)}
                        >
                          {copiedUrl === generatedUrl ? 'Copied!' : 'Copy'}
                        </button>
                      </>
                    ) : (
                      <span className="url-placeholder">Fill in required fields to generate URL</span>
                    )}
                  </div>
                  {generatedUrl && selectedStrategy.campaigns.length > 0 && (
                    <div className="add-to-campaign">
                      <span>Add to campaign:</span>
                      <div className="campaign-select-buttons">
                        {selectedStrategy.campaigns.map(campaign => (
                          <button
                            key={campaign.id}
                            className="campaign-select-btn"
                            style={{ borderColor: campaign.color }}
                            onClick={() => handleAddLinkToCampaign(campaign.id)}
                          >
                            <span className="campaign-dot" style={{ backgroundColor: campaign.color }} />
                            {campaign.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Campaigns Section */}
              <div className="campaigns-section">
                <h2>Campaigns ({selectedStrategy.campaigns.length})</h2>
                
                {selectedStrategy.campaigns.length === 0 ? (
                  <div className="no-campaigns">
                    <p>No campaigns yet. Create one to start organizing your UTM links.</p>
                    <button 
                      className="create-campaign-inline-btn"
                      onClick={() => setShowCreateCampaign(true)}
                    >
                      + Create Campaign
                    </button>
                  </div>
                ) : (
                  <div className="campaigns-list">
                    {selectedStrategy.campaigns.map(campaign => (
                      <div 
                        key={campaign.id} 
                        className="campaign-card"
                        style={{ borderLeftColor: campaign.color }}
                      >
                        <div 
                          className="campaign-header"
                          onClick={() => toggleCampaignExpanded(campaign.id)}
                        >
                          <div className="campaign-header-left">
                            <span 
                              className="expand-icon"
                            >
                              {expandedCampaigns.has(campaign.id) ? '▼' : '▶'}
                            </span>
                            <span className="campaign-name">{campaign.name}</span>
                            <span 
                              className="campaign-status"
                              style={{ backgroundColor: CAMPAIGN_STATUS_COLORS[campaign.status] }}
                            >
                              {campaign.status}
                            </span>
                            <span className="campaign-link-count">
                              {campaign.links.length} links
                            </span>
                          </div>
                          <div className="campaign-actions" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={campaign.status}
                              onChange={(e) => handleUpdateCampaignStatus(campaign.id, e.target.value as UtmCampaign['status'])}
                              className="status-select"
                            >
                              <option value="draft">Draft</option>
                              <option value="active">Active</option>
                              <option value="paused">Paused</option>
                              <option value="completed">Completed</option>
                            </select>
                            <button 
                              className="delete-campaign-btn"
                              onClick={() => handleDeleteCampaign(campaign.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        {expandedCampaigns.has(campaign.id) && (
                          <div className="campaign-content">
                            {campaign.description && (
                              <p className="campaign-description">{campaign.description}</p>
                            )}
                            
                            {campaign.links.length === 0 ? (
                              <div className="no-links">
                                <p>No links yet. Use the builder above to create one.</p>
                              </div>
                            ) : (
                              <div className="links-table">
                                <div className="links-header">
                                  <span>Base Website</span>
                                  <span>Source</span>
                                  <span>Medium</span>
                                  <span>Campaign</span>
                                  <span>Term</span>
                                  <span>Content</span>
                                  <span>URL</span>
                                  <span></span>
                                </div>
                                {campaign.links.map(link => (
                                  <div key={link.id} className="link-row">
                                    <span className="link-website">{getWebsiteLabel(link.baseUrl)}</span>
                                    <span className="link-source">{link.utmSource}</span>
                                    <span className="link-medium">{link.utmMedium}</span>
                                    <span className="link-campaign">{link.utmCampaign}</span>
                                    <span className="link-term">{link.utmTerm || '-'}</span>
                                    <span className="link-content">{link.utmContent || '-'}</span>
                                    <span className="link-url">
                                      <button 
                                        className="copy-link-btn"
                                        onClick={() => handleCopyUrl(link.generatedUrl)}
                                        title={link.generatedUrl}
                                      >
                                        {copiedUrl === link.generatedUrl ? 'Copied!' : 'Copy URL'}
                                      </button>
                                    </span>
                                    <span className="link-actions">
                                      <button 
                                        className="delete-link-btn"
                                        onClick={() => handleDeleteLink(campaign.id, link.id)}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Campaign Notes */}
                            <div className="campaign-notes">
                              <h3>Notes</h3>
                              <div className="add-note">
                                <textarea
                                  value={campaignNotes[campaign.id] || ''}
                                  onChange={(e) => setCampaignNotes(prev => ({ ...prev, [campaign.id]: e.target.value }))}
                                  placeholder="Add a note for this campaign..."
                                  rows={2}
                                />
                                <button 
                                  className="add-note-btn"
                                  onClick={() => handleAddNote(campaign.id)}
                                  disabled={!campaignNotes[campaign.id]?.trim()}
                                >
                                  Add Note
                                </button>
                              </div>
                              <div className="notes-list">
                                {(!campaign.notes || campaign.notes.length === 0) ? (
                                  <p className="no-notes">No notes yet. Add one to collaborate with your team.</p>
                                ) : (
                                  campaign.notes.map(note => (
                                    <div key={note.id} className="note-item">
                                      <div className="note-header">
                                        <span className="note-author">{note.createdByName}</span>
                                        <span className="note-date">
                                          {new Date(note.createdAt).toLocaleString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                          })}
                                        </span>
                                        <button 
                                          className="delete-note-btn"
                                          onClick={() => handleDeleteNote(campaign.id, note.id)}
                                        >
                                          ×
                                        </button>
                                      </div>
                                      <p className="note-content">{note.content}</p>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Strategy Modal */}
      {showCreateStrategy && (
        <div className="modal-overlay" onClick={() => setShowCreateStrategy(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Strategy</h2>
            <div className="form-field">
              <label>Strategy Name</label>
              <input
                type="text"
                value={newStrategyName}
                onChange={(e) => setNewStrategyName(e.target.value)}
                placeholder="e.g., Q1 2026 Marketing"
                autoFocus
              />
            </div>
            <div className="form-field">
              <label>Description (optional)</label>
              <textarea
                value={newStrategyDescription}
                onChange={(e) => setNewStrategyDescription(e.target.value)}
                placeholder="Describe the purpose of this strategy..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowCreateStrategy(false)}>
                Cancel
              </button>
              <button 
                className="create-btn"
                onClick={handleCreateStrategy}
                disabled={!newStrategyName.trim()}
              >
                Create Strategy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateCampaign && (
        <div className="modal-overlay" onClick={() => setShowCreateCampaign(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Campaign</h2>
            <div className="form-field">
              <label>Campaign Name</label>
              <input
                type="text"
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                placeholder="e.g., Facebook Spring Sale"
                autoFocus
              />
            </div>
            <div className="form-field">
              <label>Description (optional)</label>
              <textarea
                value={newCampaignDescription}
                onChange={(e) => setNewCampaignDescription(e.target.value)}
                placeholder="Describe this campaign..."
                rows={2}
              />
            </div>
            <div className="form-field">
              <label>Color</label>
              <div className="color-picker">
                {CAMPAIGN_COLORS.map(color => (
                  <button
                    key={color}
                    className={`color-btn ${selectedCampaignColor === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setSelectedCampaignColor(color)}
                  />
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowCreateCampaign(false)}>
                Cancel
              </button>
              <button 
                className="create-btn"
                onClick={handleCreateCampaign}
                disabled={!newCampaignName.trim()}
              >
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UTMStrategyBuilder;
